import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Church, Handshake, Megaphone, Inbox, Users, MessageSquare, ClipboardList } from "lucide-react";
import { Spinner } from "@/components/common/Primitives";
import { formatDateShort } from "@/lib/format";
import { getSubmissionSubmitterName } from "@/lib/submissionDisplay";
import { eventsCrud } from "@/services/eventsService";
import { unitsCrud } from "@/services/unitsService";
import { sponsorsCrud } from "@/services/sponsorsService";
import { bannersCrud } from "@/services/bannersService";
import { forumPostsCrud } from "@/services/forumsService";
import { formsCrud, formsService } from "@/services/formsService";
import { membersService } from "@/services/membersService";

const STAT_CARDS = [
  { key: "events", label: "Events", icon: Calendar, to: "/admin/events" },
  { key: "units", label: "Units", icon: Church, to: "/admin/units" },
  { key: "sponsors", label: "Sponsors", icon: Handshake, to: "/admin/sponsors" },
  { key: "banners", label: "Banners", icon: Megaphone, to: "/admin/banners" },
  { key: "posts", label: "Forum Posts", icon: MessageSquare, to: "/admin/forums" },
  { key: "forms", label: "Forms", icon: ClipboardList, to: "/admin/forms" },
  { key: "submissions", label: "Submissions", icon: Inbox, to: "/admin/submissions" },
  { key: "members", label: "Members", icon: Users, to: "/admin/members" },
];

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      eventsCrud.list(),
      unitsCrud.list(),
      sponsorsCrud.list(),
      bannersCrud.list(),
      forumPostsCrud.list(),
      formsCrud.list(),
      formsService.listSubmissions("All"),
      membersService.count(),
    ]).then(([events, units, sponsors, banners, posts, forms, submissions, members]) => {
      const len = (r) => (r.status === "fulfilled" ? r.value.length : 0);
      setStats({
        events: len(events),
        units: len(units),
        sponsors: len(sponsors),
        banners: len(banners),
        posts: len(posts),
        forms: len(forms),
        submissions: len(submissions),
        members: members.status === "fulfilled" ? members.value : 0,
      });
      setRecent(submissions.status === "fulfilled" ? submissions.value.slice(0, 5) : []);
    });
  }, []);

  if (!stats) return <Spinner className="py-24" />;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl font-semibold text-ink">Dashboard</h1>
      <p className="mt-1 text-sm text-ink-muted">An overview of everything on the BCS platform.</p>

      <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-4">
        {STAT_CARDS.map(({ key, label, icon: Icon, to }) => (
          <Link key={key} to={to} className="bg-white rounded-xl border border-gray-200 shadow-card p-5 hover:shadow-card-hover transition-shadow group" data-testid={`stat-card-${key}`}>
            <div className="flex items-center justify-between">
              <span className="h-9 w-9 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon">
                <Icon size={17} />
              </span>
            </div>
            <p className="mt-4 font-serif text-3xl font-semibold text-ink">
              {Number(stats[key]).toLocaleString("en-IN")}
            </p>
            <p className="text-xs uppercase tracking-wider text-ink-muted mt-0.5 group-hover:text-maroon transition-colors">{label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-ink">Recent submissions</h2>
          <Link to="/admin/submissions" className="text-sm font-medium text-maroon hover:underline">View all</Link>
        </div>
        {recent.length === 0 ? (
          <p className="px-6 py-8 text-sm text-ink-muted">No submissions yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {recent.map((s) => (
              <li key={s.id} className="px-6 py-4 flex items-center gap-4 text-sm">
                <span className={`h-2 w-2 rounded-full shrink-0 ${s.status === "new" ? "bg-gold" : "bg-green-600"}`} />
                <span className="font-medium text-ink flex-1 truncate">
                  {getSubmissionSubmitterName(s.data)}{" "}
                  <span className="text-ink-muted font-normal">· {s.form_slug}</span>
                </span>
                <span className="text-xs text-ink-muted">{formatDateShort(s.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
